// ===========================================
// Settings -> Enquiries (enquiries plan §5.7, L1, L5, L6, L13)
// ===========================================
// The pipeline's per-school policy: stage names and switches, who owns each stage, the wording of
// the automatic next actions, the timings, the guardrail and the two editable lists. Saved as a
// PARTIAL patch (only the keys that changed), so two people on different tabs cannot overwrite
// each other's untouched settings. Read-only without `system_settings.write`.
//
// The lists (Sources, Closing reasons) and the reply templates (Phase 7) save on their own, per
// action: anyone with `enquiries.write` may ADD; only the owner tier renames, hides or reorders (L5, L6).
// The onboarding checklist (Phase 6) saves per action too, and every change to it is the owner's.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type {
  EnquiryClientConfig,
  EnquiryFlagKey,
  EnquiryListKind,
  EnquiryListOption,
  EnquiryReplyTemplate,
  EnquirySettings,
  EnquirySettingsPatch,
  EnquiryStage,
  EnquiryTimingKey,
  NextActionKey,
  OwnedStage,
  StageOwnerTarget,
} from '../../../api/enquiry.api';
import { ENQUIRY_STAGES, TEMPLATE_BODY_MAX, TEMPLATE_NAME_MAX } from '../../../api/enquiry.api';
import {
  apiErrorMessage,
  useAddEnquiryOption,
  useAddReplyTemplate,
  useReorderReplyTemplates,
  useReplyTemplates,
  useUpdateReplyTemplate,
  useAssignableOwners,
  useEnquiryConfig,
  useEnquiryOptions,
  useEnquirySettings,
  useReorderEnquiryOptions,
  useResetEnquirySettings,
  useUpdateEnquiryOption,
  useUpdateEnquirySettings,
} from '../../../hooks/useEnquiries';
import { useCustomRoles } from '../../../hooks/useCustomRoles';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import { usePermissions } from '../../../hooks/usePermissions';
import { DEFAULT_STAGE_LABELS, stageLabel } from '../enquiries/enquiryUi';
import ReplyTemplateBodyField from '../enquiries/ReplyTemplateBodyField';
import { ENQUIRY_FLAG_LABELS, SWITCHABLE_ENQUIRY_FLAGS } from '../../../utils/enquiryFlags';
import OnboardingChecklistPanel from './OnboardingChecklistPanel';

// ===========================================
// REGISTRIES (mirror apps/backend/src/services/enquiry/enquirySettings.ts)
// ===========================================

export const STAGE_LABEL_MAX = 40;
export const NEXT_ACTION_MAX = 200;
const BALL_WITH_LABEL_MAX = 40;

const OWNED_STAGES: readonly OwnedStage[] = [
  'NEW', 'IN_CONVERSATION', 'MNG_BOOKED', 'MNG_FOLLOW_UP', 'READY_TO_ONBOARD', 'ONBOARDING', 'WAITLIST',
];
const OPTIONAL_STAGES = ['WAITLIST', 'ONBOARDING'] as const;
const MNG_STAGES: readonly OwnedStage[] = ['MNG_BOOKED', 'MNG_FOLLOW_UP'];

/** The built-in wording (shown as the placeholder; empty = this). */
export const DEFAULT_NEXT_ACTIONS: Record<NextActionKey, string> = {
  NEW_ENQUIRY: 'Initial call',
  SCHEDULE_MNG: 'Schedule Meet & Greet',
  HOLD_MNG: 'Hold Meet & Greet',
  SEND_INFO: 'Send info and class options',
  DECIDE_OUTCOME: 'Decide outcome',
  REBOOK_MNG: 'Rebook Meet & Greet',
  MNG_CANCELLED: 'Meet & Greet cancelled: follow up',
  READY_TO_ONBOARD: 'Start onboarding',
  ONBOARDING: 'Complete onboarding',
  WAITLIST: 'Check for a spot',
  FAMILY_RETURNED: 'Family replied: follow up',
  FOLLOW_UP: 'Follow up',
  REVIEW_MIGRATED: 'Review after migration',
};

const NEXT_ACTION_KEYS = Object.keys(DEFAULT_NEXT_ACTIONS) as NextActionKey[];

/** When each wording is used, so an office reads what it is changing. */
const NEXT_ACTION_LABELS: Record<NextActionKey, string> = {
  NEW_ENQUIRY: 'A new enquiry arrives',
  SCHEDULE_MNG: 'A Meet & Greet needs a time',
  HOLD_MNG: 'A Meet & Greet is booked',
  SEND_INFO: 'After a Meet & Greet: follow up',
  DECIDE_OUTCOME: 'After a Meet & Greet: not suitable',
  REBOOK_MNG: 'A family did not turn up',
  MNG_CANCELLED: 'A Meet & Greet was cancelled',
  READY_TO_ONBOARD: 'The family is ready to onboard',
  ONBOARDING: 'Onboarding has started',
  WAITLIST: 'The family is on the waitlist',
  FAMILY_RETURNED: 'The family replied',
  FOLLOW_UP: 'Any other stage, when no wording is asked for',
  REVIEW_MIGRATED: 'A migrated enquiry needs review',
};

export const TIMING_BOUNDS: Record<EnquiryTimingKey, readonly [number, number]> = {
  newDueBusinessDays: [0, 60],
  scheduleMngBusinessDays: [0, 60],
  followUpInfoBusinessDays: [0, 60],
  replyChaseBusinessDays: [0, 60],
  followUpChaseBusinessDays: [0, 60],
  defaultDueBusinessDays: [0, 60],
  newUncontactedHours: [1, 24 * 30],
  noActivityDays: [1, 365],
  hotNoActivityDays: [1, 365],
  notProceedingArchiveDays: [0, 365],
  onboardingWindowDays: [1, 365],
  migrationReviewDays: [0, 60],
};

const TIMING_FIELDS: ReadonlyArray<{ key: EnquiryTimingKey; label: string; unit: string }> = [
  { key: 'newDueBusinessDays', label: 'New enquiry: due in', unit: 'office days' },
  { key: 'scheduleMngBusinessDays', label: 'Schedule a Meet & Greet: due in', unit: 'office days' },
  { key: 'followUpInfoBusinessDays', label: 'Send info after a Meet & Greet: due in', unit: 'office days' },
  { key: 'replyChaseBusinessDays', label: 'Chase a reply: due in', unit: 'office days' },
  { key: 'followUpChaseBusinessDays', label: 'Chase a follow-up: due in', unit: 'office days' },
  { key: 'defaultDueBusinessDays', label: 'Any other next action: due in', unit: 'office days' },
  { key: 'newUncontactedHours', label: 'Flag a new enquiry nobody has contacted after', unit: 'hours' },
  { key: 'noActivityDays', label: 'Flag an enquiry with no activity for', unit: 'days' },
  { key: 'hotNoActivityDays', label: 'Flag a Hot enquiry with no activity for', unit: 'days' },
  { key: 'notProceedingArchiveDays', label: 'Archive a Not Proceeding enquiry after', unit: 'days' },
  { key: 'onboardingWindowDays', label: 'Onboarding should finish within', unit: 'days' },
  { key: 'migrationReviewDays', label: 'Review a migrated enquiry within', unit: 'days' },
];

const ISSUE_TEXT: Record<EnquiryClientConfig['ownerTargetIssues'][number]['reason'], string> = {
  ROLE_NOT_FOUND: 'the chosen role no longer exists',
  ROLE_CANNOT_READ_ENQUIRIES: 'the chosen role cannot see enquiries',
  USER_NOT_VALID: 'the chosen person is no longer active staff',
};

// ===========================================
// DRAFT (what the form holds) AND THE PATCH
// ===========================================

interface OwnerDraft {
  kind: StageOwnerTarget['kind'];
  roleId: string;
  userId: string;
}

interface Draft {
  stageLabels: Record<EnquiryStage, string>;
  stageEnabled: { WAITLIST: boolean; ONBOARDING: boolean };
  stageOwners: Record<OwnedStage, OwnerDraft>;
  nextActions: Record<NextActionKey, string>;
  timings: Record<EnquiryTimingKey, string>;
  /** Per-flag switch. */
  flags: Record<EnquiryFlagKey, boolean>;
  requireNextActionOnStageChange: boolean;
  temperatureEnabled: boolean;
  ballWithSchoolLabel: string;
}

function ownerToDraft(target: StageOwnerTarget): OwnerDraft {
  return {
    kind: target.kind,
    roleId: target.kind === 'ROLE' ? target.roleId : '',
    userId: target.kind === 'USER' ? target.userId : '',
  };
}

/** A complete target, or null while a role / person is still to be chosen. */
function draftToOwner(d: OwnerDraft): StageOwnerTarget | null {
  if (d.kind === 'ROLE') return d.roleId ? { kind: 'ROLE', roleId: d.roleId } : null;
  if (d.kind === 'USER') return d.userId ? { kind: 'USER', userId: d.userId } : null;
  return { kind: d.kind };
}

function toDraft(s: EnquirySettings): Draft {
  return {
    stageLabels: Object.fromEntries(ENQUIRY_STAGES.map((st) => [st, s.stageLabels?.[st] ?? ''])) as Draft['stageLabels'],
    stageEnabled: { WAITLIST: s.stageEnabled.WAITLIST, ONBOARDING: s.stageEnabled.ONBOARDING },
    stageOwners: Object.fromEntries(OWNED_STAGES.map((st) => [st, ownerToDraft(s.stageOwners[st])])) as Draft['stageOwners'],
    nextActions: Object.fromEntries(NEXT_ACTION_KEYS.map((k) => [k, s.nextActions?.[k] ?? ''])) as Draft['nextActions'],
    timings: Object.fromEntries(
      (Object.keys(TIMING_BOUNDS) as EnquiryTimingKey[]).map((k) => [k, String(s.timings[k])]),
    ) as Draft['timings'],
    flags: Object.fromEntries(
      SWITCHABLE_ENQUIRY_FLAGS.map((k) => [k, s.flags?.[k]?.enabled !== false]),
    ) as Draft['flags'],
    requireNextActionOnStageChange: s.requireNextActionOnStageChange,
    temperatureEnabled: s.temperatureEnabled,
    ballWithSchoolLabel: s.ballWithSchoolLabel ?? '',
  };
}

const sameTarget = (a: StageOwnerTarget, b: StageOwnerTarget): boolean => JSON.stringify(a) === JSON.stringify(b);

function timingError(key: EnquiryTimingKey, raw: string): string | null {
  const [min, max] = TIMING_BOUNDS[key];
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(n) || n < min || n > max) return `Enter a whole number from ${min} to ${max}`;
  return null;
}

interface PatchResult {
  patch: EnquirySettingsPatch;
  /** The number of changed settings. */
  changes: number;
  /** True while any value cannot be saved (a bad number, an owner still to choose). */
  invalid: boolean;
}

/** The PARTIAL patch: only the keys the draft changed from what the server holds. */
export function buildPatch(settings: EnquirySettings, draft: Draft): PatchResult {
  const patch: Record<string, unknown> = {};
  let changes = 0;
  let invalid = false;
  const put = (section: string, key: string, value: unknown) => {
    const bucket = (patch[section] ?? {}) as Record<string, unknown>;
    bucket[key] = value;
    patch[section] = bucket;
    changes += 1;
  };

  for (const st of ENQUIRY_STAGES) {
    const next = draft.stageLabels[st].trim().replace(/\s+/g, ' ');
    if (next.length > STAGE_LABEL_MAX) invalid = true;
    else if (next !== (settings.stageLabels?.[st] ?? '')) put('stageLabels', st, next || null);
  }
  for (const st of OPTIONAL_STAGES) {
    if (draft.stageEnabled[st] !== settings.stageEnabled[st]) put('stageEnabled', st, draft.stageEnabled[st]);
  }
  for (const st of OWNED_STAGES) {
    const target = draftToOwner(draft.stageOwners[st]);
    if (!target) invalid = true;
    else if (!sameTarget(target, settings.stageOwners[st])) put('stageOwners', st, target);
  }
  for (const k of NEXT_ACTION_KEYS) {
    const next = draft.nextActions[k].trim().replace(/\s+/g, ' ');
    if (next.length > NEXT_ACTION_MAX) invalid = true;
    else if (next !== (settings.nextActions?.[k] ?? '')) put('nextActions', k, next || null);
  }
  for (const k of Object.keys(TIMING_BOUNDS) as EnquiryTimingKey[]) {
    if (timingError(k, draft.timings[k])) invalid = true;
    else if (Number(draft.timings[k]) !== settings.timings[k]) put('timings', k, Number(draft.timings[k]));
  }
  for (const k of SWITCHABLE_ENQUIRY_FLAGS) {
    if (draft.flags[k] !== (settings.flags?.[k]?.enabled !== false)) put('flags', k, { enabled: draft.flags[k] });
  }
  if (draft.requireNextActionOnStageChange !== settings.requireNextActionOnStageChange) {
    patch.requireNextActionOnStageChange = draft.requireNextActionOnStageChange;
    changes += 1;
  }
  if (draft.temperatureEnabled !== settings.temperatureEnabled) {
    patch.temperatureEnabled = draft.temperatureEnabled;
    changes += 1;
  }
  const ball = draft.ballWithSchoolLabel.trim().replace(/\s+/g, ' ');
  if (ball.length > BALL_WITH_LABEL_MAX) invalid = true;
  else if (ball !== (settings.ballWithSchoolLabel ?? '')) {
    patch.ballWithSchoolLabel = ball || null;
    changes += 1;
  }

  return { patch: patch as EnquirySettingsPatch, changes, invalid };
}

// ===========================================
// OWNER PICKER
// ===========================================

function RoleSelect({
  stageName,
  value,
  disabled,
  onChange,
}: {
  stageName: string;
  value: string;
  disabled: boolean;
  onChange: (roleId: string) => void;
}) {
  const { data, isLoading, isError } = useCustomRoles();
  const roles = Array.isArray(data) ? data : [];
  return (
    <TextField
      select
      size="small"
      fullWidth
      label={`Role for ${stageName}`}
      value={roles.some((r) => r.id === value) ? value : ''}
      disabled={disabled || isLoading}
      error={isError}
      onChange={(e) => onChange(e.target.value)}
      helperText={
        isError
          ? 'Could not load the roles.'
          : 'The role must be allowed to see enquiries; the server checks it when you save.'
      }
    >
      {roles.map((r) => (
        <MenuItem key={r.id} value={r.id}>
          {r.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * The roles list (`/admin/roles`) is the owner's, so anyone else sees the stage's role by the name
 * it resolves to and cannot pick another. Never calls the owner-only route.
 */
function RoleReadOnly({ stageName, currentName, ownerTitle }: { stageName: string; currentName?: string; ownerTitle: string }) {
  return (
    <TextField
      size="small"
      fullWidth
      label={`Role for ${stageName}`}
      value={currentName ? `${currentName} (through a role)` : 'A role'}
      disabled
      helperText={`Only the ${ownerTitle} can choose a role.`}
    />
  );
}

// ===========================================
// LISTS (Sources, Closing reasons)
// ===========================================

function ListPanel({ kind, title, subheader }: { kind: EnquiryListKind; title: string; subheader: string }) {
  // Only mounted for a user who holds enquiries.read (the lists are that route's read).
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useEnquiryOptions(kind, { includeHidden: true }, can('enquiries', 'read'));
  const addMutation = useAddEnquiryOption();
  const updateMutation = useUpdateEnquiryOption();
  const reorderMutation = useReorderEnquiryOptions();
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const mayAdd = can('enquiries', 'write');
  const options: EnquiryListOption[] = Array.isArray(data) ? data : [];
  const busy = addMutation.isPending || updateMutation.isPending || reorderMutation.isPending;

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      const { existed } = await addMutation.mutateAsync({ kind, label });
      if (existed) enqueueSnackbar(`"${label}" is already in the list`, { variant: 'info' });
      setNewLabel('');
    } catch {
      // The hook shows the server's reason.
    }
  };

  const move = (index: number, delta: -1 | 1) => {
    const ids = options.map((o) => o.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    // EVERY id of the kind, hidden ones included, exactly once.
    reorderMutation.mutate({ kind, orderedIds: ids });
  };

  const saveRename = async (option: EnquiryListOption) => {
    const label = editLabel.trim();
    if (!label || label === option.label) {
      setEditingId(null);
      return;
    }
    try {
      await updateMutation.mutateAsync({ optionId: option.id, patch: { label } });
      setEditingId(null);
    } catch {
      // The hook shows the server's reason.
    }
  };

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
      <CardHeader title={title} subheader={subheader} />
      <CardContent>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : isError ? (
          <Alert severity="error">Could not load this list. Please try again.</Alert>
        ) : (
          <Stack spacing={1}>
            {options.length === 0 && <Typography color="text.secondary">Nothing here yet.</Typography>}
            {options.map((o, index) => (
              <Stack
                key={o.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ opacity: o.isActive ? 1 : 0.6, minWidth: 0 }}
              >
                {editingId === o.id ? (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={editLabel}
                      inputProps={{ maxLength: 80, 'aria-label': `New name for ${o.label}` }}
                      onChange={(e) => setEditLabel(e.target.value)}
                    />
                    <Tooltip title="Save name">
                      <span>
                        <IconButton
                          aria-label={`Save name for ${o.label}`}
                          size="small"
                          disabled={!editLabel.trim() || updateMutation.isPending}
                          onClick={() => void saveRename(o)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton aria-label="Cancel rename" size="small" onClick={() => setEditingId(null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{o.label}</Typography>
                    {o.isSystem && <Chip size="small" label="Built in" />}
                    {!o.isActive && <Chip size="small" variant="outlined" label="Hidden" />}
                    {isOwner && (
                      <>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              aria-label={`Move ${o.label} up`}
                              size="small"
                              disabled={index === 0 || busy}
                              onClick={() => move(index, -1)}
                            >
                              <UpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              aria-label={`Move ${o.label} down`}
                              size="small"
                              disabled={index === options.length - 1 || busy}
                              onClick={() => move(index, 1)}
                            >
                              <DownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!o.isSystem && (
                          <>
                            <Tooltip title="Rename">
                              <span>
                                <IconButton
                                  aria-label={`Rename ${o.label}`}
                                  size="small"
                                  disabled={busy}
                                  onClick={() => {
                                    setEditingId(o.id);
                                    setEditLabel(o.label);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={o.isActive ? 'Hide' : 'Restore'}>
                              <span>
                                <IconButton
                                  aria-label={`${o.isActive ? 'Hide' : 'Restore'} ${o.label}`}
                                  size="small"
                                  disabled={busy}
                                  onClick={() => updateMutation.mutate({ optionId: o.id, patch: { isActive: !o.isActive } })}
                                >
                                  {o.isActive ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </Stack>
            ))}
            {mayAdd && (
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={`Add to ${title.toLowerCase()}`}
                  value={newLabel}
                  inputProps={{ maxLength: 80 }}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAdd();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => void handleAdd()}
                  disabled={!newLabel.trim() || addMutation.isPending}
                >
                  Add
                </Button>
              </Stack>
            )}
            {!isOwner && (
              <Typography variant="caption" color="text.secondary">
                Only the {ownerTitle} can rename, hide or reorder.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// REPLY TEMPLATES (Phase 7, plan §12)
// ===========================================
// The same rule as the lists (P33 = L6): anyone with enquiries.write adds one ("New template", or
// "Save as template" in the reply box); only the owner tier edits, renames, hides, restores or
// reorders. Hidden ones are listed for the owner only (the server sends them only to the owner tier).

function TemplatesPanel() {
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const { data, isLoading, isError } = useReplyTemplates({ includeHidden: isOwner }, can('enquiries', 'read'));
  const updateMutation = useUpdateReplyTemplate();
  const reorderMutation = useReorderReplyTemplates();
  const [editing, setEditing] = useState<EnquiryReplyTemplate | 'new' | null>(null);

  const mayAdd = can('enquiries', 'write');
  const templates: EnquiryReplyTemplate[] = Array.isArray(data) ? data : [];
  const busy = updateMutation.isPending || reorderMutation.isPending;

  const move = (index: number, delta: -1 | 1) => {
    const ids = templates.map((t) => t.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    // EVERY template, hidden ones included, exactly once.
    reorderMutation.mutate(ids);
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title="Reply templates"
        subheader="Replies staff can start from in an enquiry's reply box. Merge fields fill in each family's details."
        action={
          mayAdd ? (
            <Button variant="outlined" size="small" onClick={() => setEditing('new')}>
              New template
            </Button>
          ) : null
        }
      />
      <CardContent>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : isError ? (
          <Alert severity="error">Could not load the templates. Please try again.</Alert>
        ) : (
          <Stack spacing={1}>
            {templates.length === 0 && (
              <Typography color="text.secondary">No templates yet. Save one from an enquiry's reply box, or add one here.</Typography>
            )}
            {templates.map((t, index) => (
              <Stack key={t.id} direction="row" spacing={1} alignItems="flex-start" sx={{ opacity: t.isActive ? 1 : 0.6, minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{t.name}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {t.body}
                  </Typography>
                  {isOwner && t.createdByName && (
                    <Typography variant="caption" color="text.secondary">
                      Added by {t.createdByName}
                    </Typography>
                  )}
                </Box>
                {!t.isActive && <Chip size="small" variant="outlined" label="Hidden" />}
                {isOwner && (
                  <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton aria-label={`Move ${t.name} up`} size="small" disabled={index === 0 || busy} onClick={() => move(index, -1)}>
                          <UpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          aria-label={`Move ${t.name} down`}
                          size="small"
                          disabled={index === templates.length - 1 || busy}
                          onClick={() => move(index, 1)}
                        >
                          <DownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton aria-label={`Edit ${t.name}`} size="small" disabled={busy} onClick={() => setEditing(t)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t.isActive ? 'Hide' : 'Restore'}>
                      <span>
                        <IconButton
                          aria-label={`${t.isActive ? 'Hide' : 'Restore'} ${t.name}`}
                          size="small"
                          disabled={busy}
                          onClick={() => updateMutation.mutate({ templateId: t.id, patch: { isActive: !t.isActive } })}
                        >
                          {t.isActive ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            ))}
            {!isOwner && (
              <Typography variant="caption" color="text.secondary">
                Only the {ownerTitle} can edit, hide or reorder a template.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
      {editing && <TemplateDialog template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

/** Add (anyone with enquiries.write) or edit (the owner tier) one template's name and text. */
function TemplateDialog({ template, onClose }: { template: EnquiryReplyTemplate | null; onClose: () => void }) {
  const [name, setName] = useState(template?.name ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const add = useAddReplyTemplate();
  const update = useUpdateReplyTemplate();
  const pending = add.isPending || update.isPending;

  const save = async () => {
    try {
      if (template) {
        await update.mutateAsync({ templateId: template.id, patch: { name: name.trim(), body: body.trim() } });
      } else {
        await add.mutateAsync({ name: name.trim(), body: body.trim() });
      }
      onClose();
    } catch {
      // The hook shows the server's reason (an unknown merge field, a name already in use).
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{template ? 'Edit template' : 'New template'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Template name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: TEMPLATE_NAME_MAX }}
            autoFocus
            fullWidth
            size="small"
          />
          <ReplyTemplateBodyField
            value={body}
            onChange={setBody}
            minRows={6}
            maxRows={14}
            helperText={`Click a field to add it where the cursor is. ${body.length}/${TEMPLATE_BODY_MAX}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={!name.trim() || !body.trim() || pending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EnquirySettingsTab() {
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const canRead = can('system_settings', 'read');
  const canWrite = can('system_settings', 'write');
  // The config, the owner picker and the lists are `enquiries.read` routes, not System Settings'.
  const canReadEnquiries = can('enquiries', 'read');

  const { data: settings, isLoading, isError } = useEnquirySettings(canRead);
  const { data: config } = useEnquiryConfig(canReadEnquiries);
  const { data: owners } = useAssignableOwners(canReadEnquiries);
  const updateMutation = useUpdateEnquirySettings();
  const resetMutation = useResetEnquirySettings();

  // The form's edits with the snapshot they were taken from; null = untouched. The patch is diffed
  // against `base`, so a refetch that brings a colleague's change never turns it into a revert (L8).
  const [edited, setEdited] = useState<{ base: EnquirySettings; draft: Draft } | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const draft = useMemo(() => edited?.draft ?? (settings ? toDraft(settings) : null), [edited, settings]);

  // When a refetch brings back exactly what was saved, hand the form back to the server's values.
  useEffect(() => {
    if (!settings) return;
    setEdited((cur) => (cur && buildPatch(settings, cur.draft).changes === 0 ? null : cur));
  }, [settings]);

  if (isLoading || (canRead && !settings && !isError)) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={160} />
        <Skeleton variant="rectangular" height={160} />
      </Stack>
    );
  }
  if (isError || !settings || !draft) {
    return <Alert severity="error">Failed to load the enquiry settings. Please try again.</Alert>;
  }

  const update = (fn: (d: Draft) => Draft) => setEdited({ base: edited?.base ?? settings, draft: fn(draft) });
  const { patch, changes, invalid } = buildPatch(edited?.base ?? settings, draft);
  const nameOf = (st: EnquiryStage) => stageLabel(config, st);
  const ownerList = Array.isArray(owners) ? owners : [];

  const handleSave = () => {
    updateMutation.mutate(patch, { onSuccess: () => setEdited(null) });
  };

  const handleReset = () => {
    resetMutation.mutate(undefined, {
      onSuccess: () => {
        setEdited(null);
        setConfirmReset(false);
      },
    });
  };

  const stageViewOf = (stage: OwnedStage) => config?.stages.find((s) => s.stage === stage);

  const ownerKindLabel = (kind: StageOwnerTarget['kind']): string => {
    switch (kind) {
      case 'ADMIN_TIER':
        return `The ${ownerTitle}`;
      case 'ROLE':
        return 'A role';
      case 'USER':
        return 'A person';
      case 'MNG_TEACHER':
        return "The Meet & Greet's teacher";
    }
  };

  return (
    <Stack spacing={3}>
      {!canWrite && (
        <Alert severity="info">View only: changing these settings needs System Settings (edit) access.</Alert>
      )}

      {/* 1. Stages */}
      <Card variant="outlined">
        <CardHeader
          title="Stages"
          subheader="What your school calls each stage. Leave a name empty to use the built-in one."
        />
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}
            >
              {ENQUIRY_STAGES.map((st) => (
                <TextField
                  key={st}
                  size="small"
                  label={`Name for ${nameOf(st)}`}
                  placeholder={DEFAULT_STAGE_LABELS[st]}
                  value={draft.stageLabels[st]}
                  disabled={!canWrite}
                  inputProps={{ maxLength: STAGE_LABEL_MAX }}
                  onChange={(e) => update((d) => ({ ...d, stageLabels: { ...d.stageLabels, [st]: e.target.value } }))}
                />
              ))}
            </Box>
            <Stack>
              {OPTIONAL_STAGES.map((st) => (
                <FormControlLabel
                  key={st}
                  label={`Use the ${nameOf(st)} stage`}
                  control={
                    <Switch
                      checked={draft.stageEnabled[st]}
                      disabled={!canWrite}
                      onChange={(e) =>
                        update((d) => ({ ...d, stageEnabled: { ...d.stageEnabled, [st]: e.target.checked } }))
                      }
                    />
                  }
                />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              MNG Booked and MNG Follow Up follow Student Onboarding&apos;s &quot;Meet &amp; Greet required&quot; setting.
              {config
                ? config.meetAndGreetEnabled
                  ? ' Meet & Greets are required now, so both stages are in use.'
                  : ' Meet & Greets are not required now, so both stages are off.'
                : ''}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* 2. Stage owners */}
      <Card variant="outlined">
        <CardHeader
          title="Stage owners"
          subheader="Who owns an enquiry at each stage, and is emailed when one arrives there."
        />
        <CardContent>
          {!canReadEnquiries ? (
            <Alert severity="info">Owners and lists need Enquiries access.</Alert>
          ) : (
          <Stack spacing={2}>
            {(config?.ownerTargetIssues ?? []).map((issue) => (
              <Alert severity="warning" key={`${issue.stage}-${issue.reason}`}>
                {nameOf(issue.stage)}: {ISSUE_TEXT[issue.reason]}. Until it is fixed the owner falls back to the {ownerTitle}.
              </Alert>
            ))}
            {OWNED_STAGES.map((st) => {
              const od = draft.stageOwners[st];
              // A role is chosen from the owner's roles list: offered to the owner, or kept as it is.
              const kinds: StageOwnerTarget['kind'][] = isOwner || od.kind === 'ROLE' ? ['ADMIN_TIER', 'ROLE', 'USER'] : ['ADMIN_TIER', 'USER'];
              if (MNG_STAGES.includes(st)) kinds.push('MNG_TEACHER');
              const current = stageViewOf(st)?.owner?.name;
              return (
                <Box key={st}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label={`Owner for ${nameOf(st)}`}
                      value={od.kind}
                      disabled={!canWrite}
                      onChange={(e) =>
                        update((d) => ({
                          ...d,
                          stageOwners: {
                            ...d.stageOwners,
                            [st]: { kind: e.target.value as StageOwnerTarget['kind'], roleId: '', userId: '' },
                          },
                        }))
                      }
                    >
                      {kinds.map((k) => (
                        <MenuItem key={k} value={k}>
                          {ownerKindLabel(k)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {od.kind === 'ROLE' && !isOwner && (
                      <RoleReadOnly stageName={nameOf(st)} currentName={current} ownerTitle={ownerTitle} />
                    )}
                    {od.kind === 'ROLE' && isOwner && (
                      <RoleSelect
                        stageName={nameOf(st)}
                        value={od.roleId}
                        disabled={!canWrite}
                        onChange={(roleId) =>
                          update((d) => ({ ...d, stageOwners: { ...d.stageOwners, [st]: { ...od, roleId } } }))
                        }
                      />
                    )}
                    {od.kind === 'USER' && (
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={`Person for ${nameOf(st)}`}
                        value={ownerList.some((o) => o.id === od.userId) ? od.userId : ''}
                        disabled={!canWrite}
                        onChange={(e) =>
                          update((d) => ({
                            ...d,
                            stageOwners: { ...d.stageOwners, [st]: { ...od, userId: e.target.value } },
                          }))
                        }
                      >
                        {ownerList.map((o) => (
                          <MenuItem key={o.id} value={o.id}>
                            {o.firstName} {o.lastName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Stack>
                  {current && (
                    <Typography variant="caption" color="text.secondary">
                      Currently: {current}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
          )}
        </CardContent>
      </Card>

      {/* 3. Next-action wording */}
      <Card variant="outlined">
        <CardHeader
          title="Next-action wording"
          subheader="What the automatic next actions say. Leave one empty to use the built-in wording."
        />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            {NEXT_ACTION_KEYS.map((k) => (
              <TextField
                key={k}
                size="small"
                label={NEXT_ACTION_LABELS[k]}
                placeholder={DEFAULT_NEXT_ACTIONS[k]}
                value={draft.nextActions[k]}
                disabled={!canWrite}
                inputProps={{ maxLength: NEXT_ACTION_MAX }}
                onChange={(e) => update((d) => ({ ...d, nextActions: { ...d.nextActions, [k]: e.target.value } }))}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* 4. Timings */}
      <Card variant="outlined">
        <CardHeader
          title="Timings"
          subheader="Office days are the days set in School Info; weekends and closed days are skipped."
        />
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            {TIMING_FIELDS.map(({ key, label, unit }) => {
              const err = timingError(key, draft.timings[key]);
              const [min, max] = TIMING_BOUNDS[key];
              return (
                <TextField
                  key={key}
                  size="small"
                  type="number"
                  label={label}
                  value={draft.timings[key]}
                  disabled={!canWrite}
                  error={!!err}
                  helperText={err ?? undefined}
                  inputProps={{ min, max, step: 1 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
                  onChange={(e) => update((d) => ({ ...d, timings: { ...d.timings, [key]: e.target.value } }))}
                />
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* 4a. The onboarding checklist (Phase 6), beside the onboarding window above. Saves per action. */}
      {can('student_onboarding', 'read') && <OnboardingChecklistPanel />}

      {/* 4b. Flags */}
      <Card variant="outlined">
        <CardHeader
          title="Flags"
          subheader="Which problems are flagged on active enquiries. The timings above decide when."
        />
        <CardContent>
          <Stack>
            {SWITCHABLE_ENQUIRY_FLAGS.map((k) => {
              // Hot, gone quiet reads the temperature, so it cannot fire while temperatures are off (review N6).
              const needsTemperature = k === 'HOT_QUIET' && !draft.temperatureEnabled;
              return (
                <Box key={k}>
                  <FormControlLabel
                    label={`Flag: ${ENQUIRY_FLAG_LABELS[k]}`}
                    control={
                      <Switch
                        checked={draft.flags[k]}
                        disabled={!canWrite || needsTemperature}
                        onChange={(e) => update((d) => ({ ...d, flags: { ...d.flags, [k]: e.target.checked } }))}
                      />
                    }
                  />
                  {needsTemperature && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 6, mt: -0.5 }}>
                      Needs temperatures switched on (see Moving an enquiry below).
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* 5. Guardrail, temperature, Ball with */}
      <Card variant="outlined">
        <CardHeader title="Moving an enquiry" />
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              label="A stage move must say who, what and by when"
              control={
                <Switch
                  checked={draft.requireNextActionOnStageChange}
                  disabled={!canWrite}
                  onChange={(e) => update((d) => ({ ...d, requireNextActionOnStageChange: e.target.checked }))}
                />
              }
            />
            <FormControlLabel
              label="Show enquiry temperature (cold, warm, hot)"
              control={
                <Switch
                  checked={draft.temperatureEnabled}
                  disabled={!canWrite}
                  onChange={(e) => update((d) => ({ ...d, temperatureEnabled: e.target.checked }))}
                />
              }
            />
            <TextField
              size="small"
              label="Ball with: the school is called"
              placeholder="The school's name"
              value={draft.ballWithSchoolLabel}
              disabled={!canWrite}
              inputProps={{ maxLength: BALL_WITH_LABEL_MAX }}
              helperText="Shown on the Ball with tag. Leave empty to use the school's name."
              onChange={(e) => update((d) => ({ ...d, ballWithSchoolLabel: e.target.value }))}
            />
          </Stack>
        </CardContent>
      </Card>

      {updateMutation.isError && (
        <Alert severity="error">{apiErrorMessage(updateMutation.error, 'Failed to save the enquiry settings')}</Alert>
      )}

      {canWrite && (
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ResetIcon />}
            onClick={() => setConfirmReset(true)}
            disabled={resetMutation.isPending || updateMutation.isPending}
          >
            Reset to defaults
          </Button>
          <Button variant="text" onClick={() => setEdited(null)} disabled={changes === 0 || updateMutation.isPending}>
            Discard
          </Button>
          <Button
            variant="contained"
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={changes === 0 || invalid || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      )}

      {/* 6. Lists */}
      {canReadEnquiries ? (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <ListPanel kind="SOURCE" title="Sources" subheader="Where enquiries come from (marketing source)." />
          <ListPanel kind="CLOSED_REASON" title="Closing reasons" subheader="Why an enquiry did not go ahead." />
        </Stack>
      ) : null}

      {/* 7. Reply templates (Phase 7; Laura 26/10/2026: managed here, beside the lists) */}
      {canReadEnquiries ? <TemplatesPanel /> : null}

      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>Reset enquiry settings?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every enquiry setting on this tab goes back to the built-in default: names, owners, wording, timings and
            switches. The lists of sources and closing reasons are not changed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)} disabled={resetMutation.isPending}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleReset} disabled={resetMutation.isPending}>
            {resetMutation.isPending ? 'Resetting...' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
